import React, { useImperativeHandle, useState } from 'react'
import { ethers } from 'ethers'
import { useTranslation } from 'react-i18next'

import { getWalletSnapshot } from '../../../../utils/walletProviders'
import { nftAbi } from '../../../../utils/config'
import { uploadText } from '../../../../utils/ipfs'
import { getLocalizedWalletError } from '../../../NFTConsole/errorMessages'
import ConsoleDialog from '../../../NFTConsole/ConsoleDialog'
import FileUploader from '../FileUploader/index.jsx'

const SOCIAL_FIELDS = [
    { key: 'website', label: '官网', icon: 'fa fa-globe' },
    { key: 'twitter', label: '推特', icon: 'fab fa-twitter' },
    { key: 'telegram', label: 'Telegram', icon: 'fab fa-telegram-plane' },
    { key: 'youtube', label: 'Youtube', icon: 'fab fa-youtube' },
    { key: 'reddit', label: 'Reddit', icon: 'fab fa-reddit-alien' },
    { key: 'facebook', label: 'Facebook', icon: 'fab fa-facebook-f' },
    { key: 'github', label: 'Github', icon: 'fab fa-github' },
    { key: 'discord', label: 'Discord', icon: 'fab fa-discord' },
]

const SECTION_DEFAULTS = {
    base: true,
    description: true,
    community: true,
}

export default function IpfsPanel(props) {
    const { t } = useTranslation()
    const [errorMsg, setErrorMsg] = useState('')
    const [currentProvider, setCurrentProvider] = useState(null)
    const [ipfsInfo, setIpfsInfo] = useState({ project_name: '', extra: [] })
    const [ipfsLoading, setIpfsLoading] = useState(false)
    const [logoUploading, setLogoUploading] = useState(false)
    const [isDirty, setIsDirty] = useState(false)
    const [openSections, setOpenSections] = useState(SECTION_DEFAULTS)

    const setIpfsInfoLogic = (info) => new Promise((resolve) => {
        setIpfsInfo({ project_name: '', extra: [], ...info })
        setErrorMsg('')
        setLogoUploading(false)
        setIsDirty(false)
        setOpenSections(SECTION_DEFAULTS)
        resolve()
    })

    useImperativeHandle(props.onRef, () => ({
        setIpfsInfo: setIpfsInfoLogic,
    }))

    const initProvider = () => {
        if (currentProvider) return currentProvider

        const injectedProvider = getWalletSnapshot().provider
        if (!injectedProvider) return null

        const provider = new ethers.providers.Web3Provider(injectedProvider)
        setCurrentProvider(provider)
        return provider
    }

    const updateInfo = (key, value) => {
        setIpfsInfo((current) => ({ ...current, [key]: value }))
        setIsDirty(true)
    }

    const updateExtra = (index, key, value) => {
        setIpfsInfo((current) => ({
            ...current,
            extra: (current.extra || []).map((item, itemIndex) => (
                itemIndex === index ? { ...item, [key]: value } : item
            )),
        }))
        setIsDirty(true)
    }

    const removeExtra = (index) => {
        setIpfsInfo((current) => ({
            ...current,
            extra: (current.extra || []).filter((_item, itemIndex) => itemIndex !== index),
        }))
        setIsDirty(true)
    }

    const toggleSection = (section) => {
        setOpenSections((current) => ({ ...current, [section]: !current[section] }))
    }

    const handleSubmit = async () => {
        if (ipfsLoading) return

        setErrorMsg('')
        setIpfsLoading(true)
        try {
            const uploaded = await uploadText(JSON.stringify(ipfsInfo))
            const provider = initProvider()
            if (!provider) throw new Error(t('未检测到可用钱包'))

            const contract = new ethers.Contract(props.contract, nftAbi, provider.getSigner())
            const tx = await contract.setProjectIpfs(uploaded.path)
            await tx.wait()
            setIsDirty(false)
            props.ok?.(uploaded)
        } catch (error) {
            setErrorMsg(getLocalizedWalletError(error, t))
        } finally {
            setIpfsLoading(false)
        }
    }

    const renderSectionHeader = (number, title, section) => (
        <button
            type="button"
            className="project-info-section__toggle"
            aria-expanded={openSections[section]}
            onClick={() => toggleSection(section)}
        >
            <span className="project-info-section__number">{number}</span>
            <strong>{t(title)}</strong>
            <i className={`fa fa-chevron-${openSections[section] ? 'up' : 'down'}`} aria-hidden="true" />
        </button>
    )

    const footer = (
        <>
            <div className={`project-info-dialog__status ${isDirty ? 'is-dirty' : ''}`}>
                <span aria-hidden="true" />
                {t(isDirty ? '存在未保存的修改' : '尚未修改')}
            </div>
            <div className="project-info-dialog__actions">
                <button type="button" className="console-button is-secondary" onClick={props.handleClose} disabled={ipfsLoading}>
                    {t('取消')}
                </button>
                <button type="button" className="console-button is-primary" onClick={handleSubmit} disabled={ipfsLoading || logoUploading}>
                    {(ipfsLoading || logoUploading) && <i className="fa fa-spinner loading-css" aria-hidden="true" />}
                    {t(logoUploading ? '图片上传中' : '保存')}
                </button>
            </div>
        </>
    )

    return (
        <ConsoleDialog
            show={props.show}
            onClose={props.handleClose}
            title={t('设置项目信息')}
            description={t('更新项目名称、品牌图片与社区入口')}
            icon="fa-edit"
            closeLabel={t('关闭')}
            className="project-info-dialog"
            bodyClassName="project-info-dialog__scroll"
            footer={footer}
        >
            <form className="project-info-form" onSubmit={(event) => event.preventDefault()}>
                {errorMsg && (
                    <div className="project-info-form__error" role="alert">
                        <i className="fa fa-exclamation-circle" aria-hidden="true" />
                        <span>{errorMsg}</span>
                    </div>
                )}

                <section className="project-info-section">
                    {renderSectionHeader('01', '基础资料', 'base')}
                    {openSections.base && (
                        <div className="project-info-section__content">
                            <label className="project-info-field" htmlFor="project-info-name">
                                <span>{t('项目名称')}</span>
                                <input
                                    id="project-info-name"
                                    type="text"
                                    value={ipfsInfo.project_name || ''}
                                    onChange={(event) => updateInfo('project_name', event.target.value)}
                                />
                            </label>

                            <div className="project-info-logo-row">
                                <div className="project-info-logo-row__preview">
                                    <span>{t('项目LOGO')}</span>
                                    <FileUploader
                                        onBeforeUpload={(file) => {
                                            if (file.type.indexOf('image') === -1) {
                                                setErrorMsg(t('文件格式不正确'))
                                                return false
                                            }
                                            if (file.size > 1024 * 200) {
                                                setErrorMsg(t('文件大小不能超过500kb').replace('{size}', '200'))
                                                return false
                                            }
                                            return true
                                        }}
                                        url={ipfsInfo.logo || ''}
                                        width="92px"
                                        height="92px"
                                        alt={t('项目LOGO')}
                                        onUploadingChange={setLogoUploading}
                                        onFileUpload={(url) => updateInfo('logo', url)}
                                        onUploadError={() => setErrorMsg(t('图片上传失败，请重试'))}
                                    />
                                </div>
                                <div className="project-info-logo-row__copy">
                                    <strong>{t('上传项目 Logo')}</strong>
                                    <p>{t('支持 JPG、PNG、WEBP，建议 512 x 512px，文件不超过 200KB')}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </section>

                <section className="project-info-section">
                    {renderSectionHeader('02', '项目简介', 'description')}
                    {openSections.description && (
                        <div className="project-info-section__content">
                            <label className="project-info-field" htmlFor="project-info-description">
                                <span>{t('项目简介')}</span>
                                <textarea
                                    id="project-info-description"
                                    rows={5}
                                    value={ipfsInfo.description || ''}
                                    onChange={(event) => updateInfo('description', event.target.value)}
                                />
                            </label>
                        </div>
                    )}
                </section>

                <section className="project-info-section">
                    {renderSectionHeader('03', '社区链接', 'community')}
                    {openSections.community && (
                        <div className="project-info-section__content">
                            <div className="project-info-social-grid">
                                {SOCIAL_FIELDS.map((field) => (
                                    <label className="project-info-field" htmlFor={`project-info-${field.key}`} key={field.key}>
                                        <span><i className={field.icon} aria-hidden="true" />{t(field.label)}</span>
                                        <input
                                            id={`project-info-${field.key}`}
                                            type="url"
                                            inputMode="url"
                                            value={ipfsInfo[field.key] || ''}
                                            onChange={(event) => updateInfo(field.key, event.target.value)}
                                            placeholder="https://"
                                        />
                                    </label>
                                ))}
                            </div>

                            {(ipfsInfo.extra || []).length > 0 && (
                                <div className="project-info-extra-list">
                                    <strong>{t('自定义字段')}</strong>
                                    {(ipfsInfo.extra || []).map((item, index) => (
                                        <div className="project-info-extra" key={index}>
                                            <label className="project-info-field" htmlFor={`project-extra-name-${index}`}>
                                                <span>{t('字段名称')}</span>
                                                <input
                                                    id={`project-extra-name-${index}`}
                                                    value={item.name || ''}
                                                    onChange={(event) => updateExtra(index, 'name', event.target.value)}
                                                />
                                            </label>
                                            <label className="project-info-field" htmlFor={`project-extra-content-${index}`}>
                                                <span>{t('字段内容')}</span>
                                                <textarea
                                                    id={`project-extra-content-${index}`}
                                                    rows={2}
                                                    value={item.content || ''}
                                                    onChange={(event) => updateExtra(index, 'content', event.target.value)}
                                                />
                                            </label>
                                            <button type="button" className="project-info-extra__remove" onClick={() => removeExtra(index)} aria-label={t('删除字段')}>
                                                <i className="fa fa-trash-alt" aria-hidden="true" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <button
                                type="button"
                                className="project-info-add-field"
                                onClick={() => {
                                    setIpfsInfo((current) => ({
                                        ...current,
                                        extra: [...(current.extra || []), { name: '', content: '' }],
                                    }))
                                    setIsDirty(true)
                                }}
                            >
                                <i className="fa fa-plus" aria-hidden="true" />
                                {t('添加字段')}
                            </button>
                        </div>
                    )}
                </section>
            </form>
        </ConsoleDialog>
    )
}
